import { tKey } from "@/core/i18n";
import {
  EvalActionTypeEnum,
  EvalGpioStateEnum,
  type EvalAction,
} from "@repo/schema";
import { BellRingIcon, TriangleAlertIcon, ZapIcon } from "lucide-react";
import { formatGpioAction } from "../../utils/limitFormatters";
import { GPIO_PINS, isGpioAction, type ActionDescriptor } from "./actionTypes";
import { GpioActionFields } from "./GpioActionFields";

const usedPins = (actions: EvalAction[]) =>
  new Set(actions.filter(isGpioAction).map((action) => action.pin));

/** Add a new action type by appending a descriptor here. */
export const actionDescriptors: ActionDescriptor[] = [
  {
    type: EvalActionTypeEnum.ALERT,
    label: tKey("evaluation:actions.type.ALERT.label"),
    description: tKey("evaluation:actions.type.ALERT.description"),
    Icon: BellRingIcon,
    badgeVariant: "destructive",
    createDefault: () => ({ type: EvalActionTypeEnum.ALERT }),
    replaces: [EvalActionTypeEnum.WARNING],
  },
  {
    type: EvalActionTypeEnum.WARNING,
    label: tKey("evaluation:actions.type.WARNING.label"),
    description: tKey("evaluation:actions.type.WARNING.description"),
    Icon: TriangleAlertIcon,
    badgeVariant: "secondary",
    createDefault: () => ({ type: EvalActionTypeEnum.WARNING }),
    replaces: [EvalActionTypeEnum.ALERT],
  },
  {
    type: EvalActionTypeEnum.GPIO,
    label: tKey("evaluation:actions.type.GPIO.label"),
    description: tKey("evaluation:actions.type.GPIO.description"),
    Icon: ZapIcon,
    badgeVariant: "outline",
    createDefault: (actions) => ({
      type: EvalActionTypeEnum.GPIO,
      pin: GPIO_PINS.find((pin) => !usedPins(actions).has(pin)) ?? GPIO_PINS[0],
      state: EvalGpioStateEnum.HIGH,
      durationMs: null,
    }),
    summarize: (action, t) => (isGpioAction(action) ? formatGpioAction(action, t) : ""),
    Fields: GpioActionFields,
    unavailableReason: (actions) =>
      usedPins(actions).size >= GPIO_PINS.length
        ? tKey("evaluation:gpio.bothPinsUsed")
        : null,
  },
];

export function descriptorFor(
  type: EvalActionTypeEnum,
): ActionDescriptor | undefined {
  return actionDescriptors.find((descriptor) => descriptor.type === type);
}

/** True when the type has an inline settings panel, i.e. the row can expand. */
export function isConfigurable(type: EvalActionTypeEnum): boolean {
  return !!descriptorFor(type)?.Fields;
}
